import raw from '@/data/sources/pulse/stage_0_raw.json';
import attributed from '@/data/sources/pulse/stage_1_attributed.json';
import collapsed from '@/data/sources/pulse/stage_2_collapsed.json';
import jackova from '@/data/sources/pulse/stage_3_jackova.json';
import bitwala from '@/data/sources/pulse/stage_4_bitwala.json';
import chartReady from '@/data/sources/pulse/chart_ready.json';

export type Stage =
  | 'empty'
  | 'raw'
  | 'attributed'
  | 'collapsed'
  | 'jackova'
  | 'bitwala'
  | 'published';

export type Buckets = { pro: number[]; per: number[] };

type Snap = {
  start: number | null;
  end: number | null;
  projects: {
    id: string;
    category: 'professional' | 'personal';
    points: number[][];
  }[];
};

const EMPTY: Snap = {
  start: chartReady.start,
  end: chartReady.end,
  projects: [],
};

export const STAGES: Record<Stage, { snap: Snap; log: boolean }> = {
  empty: { snap: EMPTY, log: true },
  raw: { snap: raw as unknown as Snap, log: false },
  attributed: { snap: attributed as unknown as Snap, log: false },
  collapsed: { snap: collapsed as unknown as Snap, log: false },
  jackova: { snap: jackova as unknown as Snap, log: false },
  bitwala: { snap: bitwala as unknown as Snap, log: false },
  published: { snap: chartReady as unknown as Snap, log: true },
};

const STAGE_LIST = Object.keys(STAGES) as Stage[];

const DOMAIN_START = chartReady.start ?? 0;
const DOMAIN_END = chartReady.end ?? 1;
const SPAN = Math.max(1, DOMAIN_END - DOMAIN_START);

export const dateSeconds = (d: string) => Date.parse(d + 'T00:00:00Z') / 1000;

export const frac = (t: number) =>
  Math.min(1, Math.max(0, (t - DOMAIN_START) / SPAN));

const bucketize = (snap: Snap, n: number, log: boolean): Buckets => {
  const pro = new Array(n).fill(0);
  const per = new Array(n).fill(0);
  for (const p of snap.projects) {
    const tgt = p.category === 'professional' ? pro : per;
    for (const pt of p.points) {
      const b = Math.min(n - 1, Math.max(0, Math.floor(frac(pt[0]) * n)));
      tgt[b] += pt[pt.length - 1];
    }
  }
  const f = (x: number) => (log ? Math.log(1 + x) : x);
  return { pro: pro.map(f), per: per.map(f) };
};

export type StageColumns = Record<Stage, Buckets>;

export const bucketizeAll = (n: number): StageColumns => {
  const out = {} as StageColumns;
  for (const stage of STAGE_LIST) {
    out[stage] = bucketize(STAGES[stage].snap, n, STAGES[stage].log);
  }
  return out;
};

export const yearTicks = (n: number) => {
  const out: { yr: number; px: number }[] = [];
  for (let yr = 2014; yr <= 2026 && n > 1; yr++) {
    const t = Date.UTC(yr, 0, 1) / 1000;
    if (t < DOMAIN_START || t > DOMAIN_END) continue;
    out.push({ yr, px: Math.floor(frac(t) * n) * 2 });
  }
  return out;
};
